<template>
  <v-navigation-drawer app v-model="drawer" width="300px">
    <v-list-item class="pt-3 pb-2" v-if="isAuthenticated && navigationSettings.showMmmmintLogo" to="/">
      <!-- `eager` and no transition, so that the logo does not fade in again on every navigation -->
      <v-img :src="logo" alt="logo" contain eager :transition="false"></v-img>
    </v-list-item>

    <v-list dense expand shaped class="pr-3" v-if="partnerId">
      <template v-for="(nav, i) in items">
        <v-subheader :key="i" class="pl-4">{{ $t("navCategory." + nav.category) }}</v-subheader>

        <template v-for="(item, idx) in nav.items">
          <the-navigation-sub-group
            v-if="subGroup(item)"
            :key="nav.category + idx"
            :item="item"
            :subGroup="subGroup(item)"
          />

          <v-list-item v-else :key="nav.category + idx" :to="to(item)" color="primary">
            <v-list-item-icon v-if="item.navigation" class="mr-4">
              <v-icon>{{ icon(item) }}</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title class="font-weight-regular text-body-2">
                {{ item.title }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="isDebug && item.navigation">
              <v-chip x-small>
                {{ item.navigation.priority }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </template>
      </template>
    </v-list>

    <debug>
      <v-list dense>
        <v-list-item v-for="(item, idx) in navigationRoutes" :key="'debug' + idx">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.navigation">{{ item.navigation.priority }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </debug>

    <template v-slot:append>
      <the-settings-list></the-settings-list>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts">
import TheSettingsList from "@/layouts/navigation/TheSettingsList.vue";
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { NavigationRoute } from "@/store/mapper/navigation.route";
import { ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { NavigationModule } from "@/store/modules/navigation.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component } from "vue-property-decorator";
import { RawLocation } from "vue-router";
import Debug from "@/components/utility/Debug.vue";
import { UiModule } from "@/store/modules/ui.store";
import { FeatureModule } from "@/store/modules/feature.store";
import TheNavigationSubGroup from "./navigation/TheNavigationSubGroup.vue";
import { getNavigationSubGroup } from "./navigation/navigationSubGroups";
import { INavigationSubGroup } from "@/lib/interfaces/navigation-sub-group.interface";
import NavigationDrawerScrollMixin from "@/mixins/NavigationDrawerScrollMixin.vue";
import { resolveNavigationIcon } from "@/lib/utility/navigation-icon";

@Component({
  components: {
    TheSettingsList,
    Debug,
    TheNavigationSubGroup
  }
})
export default class TheNavigationBaseV2 extends NavigationDrawerScrollMixin {
  ActionEnum = ActionEnum;
  ResourceEnum = ResourceEnum;
  selectedItem = 1;

  get drawer() {
    return UiModule.drawer;
  }

  set drawer(val: boolean | null) {
    UiModule.setDrawer(val);
  }

  get logo() {
    return AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.damageReportLogo);
  }

  get navigationSettings() {
    return ConfigModule.navigationSettings;
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get partnerId() {
    return this.partner?.id ?? "";
  }

  get items() {
    if (!this.partnerId) {
      return [];
    }

    return NavigationModule.sideBarNavigationWithSubheader;
  }

  get navigationRoutes() {
    return NavigationModule.routes
      .filter((r) => r.navigation)
      .sort(function (a, b) {
        return (b.navigation?.priority || 0) - (a.navigation?.priority || 0);
      });
  }

  get isDebug() {
    return ConfigModule.debug;
  }

  /**
   * The icon of an entry, resolved on every render so that an entry configured with a dynamic icon
   * - e.g. the inbox, whose icon reflects unread messages - re-renders when its state changes.
   */
  icon(item: NavigationRoute): string | undefined {
    return resolveNavigationIcon(item.navigation?.icon);
  }

  login() {
    this.$router.push({ name: "Login" });
  }

  to(item: NavigationRoute): RawLocation {
    return { name: item.name, params: { partnerId: this.partnerId } };
  }

  /**
   * Sub groups are behind a feature flag while they are being tested. Without it every
   * navigation entry stays the flat link it was before.
   */
  get isNavigationSubGroupsEnabled() {
    return FeatureModule.isNavigationSubGroupsEnabled;
  }

  /**
   * @param item a navigation entry
   * @returns how the entry resolves its children, if it is rendered as an expandable group
   */
  subGroup(item: NavigationRoute): INavigationSubGroup | undefined {
    if (!this.isNavigationSubGroupsEnabled) {
      return undefined;
    }

    return getNavigationSubGroup(item.name);
  }
}
</script>
<style>
.navigation-base-box-shadow {
  box-shadow: 0 5px 10px -4px rgb(94 86 105 / 42%);
}
</style>
